import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/supabase';
import { useAuth } from '../../lib/auth-context';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft,
  Users,
  ShieldCheck,
  Upload,
  Loader2,
  Calendar as CalendarIcon,
  MapPin,
  Ruler,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { MapPointPicker } from '../../components/common/MapPointPicker';
import { LocationAutocomplete } from '../../components/common/LocationAutocomplete';
import { CheckpointEditor } from '../../components/common/CheckpointEditor';
import { BankAccountFields } from '../../components/common/BankAccountFields';
import { bandForCapacity } from '../../lib/capacity-bands';
import { useFxRate, formatPriceUSD } from '../../lib/use-fx';
import { getUserCurrency, countryToISO2 } from '../../lib/country';

const STEPS = ['type', 'basics', 'options', 'review'];

// `datetime-local` min must be in the user's local TZ (yyyy-MM-ddTHH:mm).
function nowDateTimeLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function MobileCreateEvent() {
  const nav = useNavigate();
  const { refreshProfile, profile } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  // 'free' | 'custom' — only on Step 2 (basics)
  const [capacityMode, setCapacityMode] = useState('free');
  const [form, setForm] = useState({
    type: 'community',
    eventMode: 'physical', // 'physical' | 'virtual' (community-only)
    name: '',
    location: '',
    locationLat: null,
    locationLng: null,
    date: '',
    joinDeadline: '',
    distance: '5',
    capacity: '299',
    isPaid: false,
    fee: '',
    bankName: '',
    accountNumber: '',
    accountName: '',
    description: '',
    bibMode: 'none',
    medalEnabled: false,
    activationCode: '',
    image: '',
    startLat: null,
    startLng: null,
    finishLat: null,
    finishLng: null,
    checkpointsEnabled: false,
    checkpoints: [],
  });

  // Currency conversion for the participant tier badge
  const userCurrency = getUserCurrency(profile?.country || 'Nigeria');
  const fx = useFxRate(userCurrency.code);
  const band = bandForCapacity(form.capacity);

  // ISO-2 code (lowercased) of the organizer's saved country — used to bias
  // location suggestions to their region. Defaults to 'ng' if we can't map
  // it (which is the realistic short-term userbase anyway).
  const userCountryCC = (countryToISO2(profile?.country) || 'NG').toLowerCase();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => (step === 0 ? confirmAndLeave(-1) : setStep((s) => s - 1));

  // Detect whether the user has actually started filling out the form.
  // Heuristic: any text field non-empty OR the map has a Start/Finish.
  const formIsDirty =
    Boolean(form.name?.trim()) ||
    Boolean(form.location?.trim()) ||
    Boolean(form.date) ||
    Boolean(form.description?.trim()) ||
    Boolean(form.image) ||
    form.startLat != null ||
    form.finishLat != null ||
    (form.checkpoints?.length || 0) > 0;

  // Pending navigation request — when set, we show the leave confirm dialog
  // and only navigate if the user confirms. Holds either an absolute path
  // (string) or a navigate(-1) delta.
  const [pendingNav, setPendingNav] = useState(null);
  function confirmAndLeave(target) {
    if (!formIsDirty || busy) {
      if (typeof target === 'number') nav(target);
      else nav(target);
      return;
    }
    setPendingNav(target);
  }

  // Intercept clicks on internal links/buttons that would navigate away.
  // We hook this at the document level so we don't have to wire it through
  // BottomTabs / MobileLayout. Bottom-tab and header anchors live inside
  // <a> elements with href, so we catch them at the capture phase.
  useEffect(() => {
    if (!formIsDirty || busy) return;
    function onClick(e) {
      // Ignore right/middle/keyboard-modified clicks (open in new tab etc.)
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      // Same path — let it through.
      if (href === window.location.pathname) return;
      // Don't intercept clicks within the create-event card itself.
      if (a.closest('[data-testid="leave-confirm-dialog"]')) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNav(href);
    }
    document.addEventListener('click', onClick, true);
    // Also warn on hard reloads / external nav.
    function onBeforeUnload(ev) {
      ev.preventDefault();
      ev.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [formIsDirty, busy]);
  const minDateLocal = nowDateTimeLocal();

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');
    setUploading(true);
    try {
      const url = await api.uploadEventImage(file);
      update('image', url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    setBusy(true);
    try {
      await api.createEvent(form);
      toast.success('Event created');
      // Refresh profile so the new 'organizer' role (auto-applied by the
      // handle_event_created trigger) is reflected in the UI immediately.
      try { await refreshProfile(); } catch { /* ignore */ }
      nav('/events');
    } catch (err) {
      toast.error(err.message || 'Failed to create event');
    } finally {
      setBusy(false);
    }
  };

  const isVirtual = form.type === 'community' && form.eventMode === 'virtual';
  const canContinueBasics = form.name && form.date && Number(form.distance) > 0 && (isVirtual || form.location);

  return (
    <div className="min-h-full flex flex-col bg-background" data-testid="mobile-create-event">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={goBack}
          className="h-9 w-9 rounded-md border border-border flex items-center justify-center"
          data-testid="ce-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1">
          <p className="rivl-label text-[9px]">Create event</p>
          <p className="font-display text-sm font-semibold leading-tight">
            Step {step + 1} of {STEPS.length}
          </p>
        </div>
        <Stepper current={step} total={STEPS.length} />
      </div>

      <div className="flex-1 px-4 py-5 space-y-5">
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Pick a vibe</h2>
              <p className="text-xs text-muted-foreground mt-1">
                You can change verification settings later.
              </p>
            </div>
            <TypeCard
              testid="ce-type-community"
              active={form.type === 'community'}
              onClick={() => update('type', 'community')}
              icon={Users}
              title="Community"
              points={['Run with friends', 'No bib required', 'Physical or virtual']}
            />
            <TypeCard
              testid="ce-type-official"
              active={form.type === 'official'}
              onClick={() => update('type', 'official')}
              icon={ShieldCheck}
              title="Official"
              points={['Validated bibs', 'Full GPS verification', 'Medal token issuance']}
              primary
            />

            {/* Sub-mode picker: only when Community is selected */}
            {form.type === 'community' && (
              <div className="space-y-2 pt-2" data-testid="ce-community-mode">
                <p className="rivl-label text-[10px]">Format</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => update('eventMode', 'physical')}
                    data-testid="ce-mode-physical"
                    className={`rounded-md border-2 p-3 text-left transition-colors ${form.eventMode === 'physical' ? 'border-primary bg-primary/5' : 'border-border'}`}
                  >
                    <div className="font-semibold text-sm">Physical</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Location + checkpoints</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => update('eventMode', 'virtual')}
                    data-testid="ce-mode-virtual"
                    className={`rounded-md border-2 p-3 text-left transition-colors ${form.eventMode === 'virtual' ? 'border-primary bg-primary/5' : 'border-border'}`}
                  >
                    <div className="font-semibold text-sm">Virtual</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Distance only, run anywhere</div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">The basics</h2>
              <p className="text-xs text-muted-foreground mt-1">Name it, place it, time it.</p>
            </div>

            <FieldMobile label="Event name">
              <Input
                data-testid="ce-name"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                className="h-12 rounded-md text-base"
              />
            </FieldMobile>

            {!(form.type === 'community' && form.eventMode === 'virtual') && (
              <FieldMobile label="Location" icon={MapPin}>
                <LocationAutocomplete
                  data-testid="ce-location"
                  value={form.location}
                  onChange={(v) => update('location', v)}
                  country={userCountryCC}
                  onPlace={(place) => {
                    const loc = place?.geometry?.location;
                    if (loc) setForm((f) => ({ ...f, locationLat: loc.lat(), locationLng: loc.lng() }));
                  }}
                  placeholder="Search city, venue, or landmark"
                  className="h-12 rounded-md text-base"
                />
              </FieldMobile>
            )}

            <FieldMobile label="Date & time" icon={CalendarIcon}>
              <Input
                data-testid="ce-date"
                type="datetime-local"
                value={form.date}
                onChange={(e) => update('date', e.target.value)}
                min={minDateLocal}
                className="h-12 rounded-md text-base"
              />
            </FieldMobile>

            <FieldMobile label="Registration deadline (optional)" icon={CalendarIcon}>
              <Input
                data-testid="ce-join-deadline"
                type="datetime-local"
                value={form.joinDeadline}
                onChange={(e) => update('joinDeadline', e.target.value)}
                min={minDateLocal}
                max={form.date || undefined}
                className="h-12 rounded-md text-base"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                After this time, runners can no longer join. Defaults to event start.
              </p>
            </FieldMobile>

            <div className="grid grid-cols-2 gap-3">
              <FieldMobile label="Distance (km)" icon={Ruler}>
                <Input
                  data-testid="ce-distance"
                  type="number"
                  inputMode="decimal"
                  value={form.distance}
                  onChange={(e) => update('distance', e.target.value)}
                  className="h-12 rounded-md text-base font-mono"
                />
              </FieldMobile>
              <FieldMobile label="Participants">
                <select
                  data-testid="ce-capacity-mode"
                  value={capacityMode}
                  onChange={(e) => {
                    const m = e.target.value;
                    setCapacityMode(m);
                    if (m === 'free') update('capacity', '299');
                    else update('capacity', '');
                  }}
                  className="w-full h-12 rounded-md border border-border bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="free">Free · 0–299</option>
                  <option value="custom">Custom</option>
                </select>
                {capacityMode === 'custom' && (
                  <div className="mt-2 space-y-2">
                    <Input
                      data-testid="ce-capacity-custom"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={form.capacity}
                      onChange={(e) => update('capacity', e.target.value)}
                      placeholder="e.g. 1500"
                      className="h-12 rounded-md text-base font-mono"
                    />
                    {band && (
                      <div
                        className="flex items-center justify-between text-[11px] px-3 py-2 rounded-md bg-secondary/50 border border-border"
                        data-testid="ce-capacity-band-readout"
                      >
                        <span className="text-muted-foreground">
                          {band.tier} · {band.from === 0 ? '0' : band.from.toLocaleString()}
                          –{Number.isFinite(band.to - 1) ? (band.to - 1).toLocaleString() : '∞'}
                        </span>
                        <span className="font-mono font-semibold" data-testid="ce-capacity-price">
                          {fx.loading ? '…' : formatPriceUSD(band.usd, userCurrency, fx.rate)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </FieldMobile>
            </div>

            <FieldMobile label="Entry">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => update('isPaid', false)}
                  data-testid="ce-entry-free"
                  className={`h-9 rounded-md border-2 text-xs font-semibold uppercase tracking-widest ${
                    !form.isPaid ? 'border-success bg-success/5 text-success' : 'border-border'
                  }`}
                >
                  Free
                </button>
                <button
                  type="button"
                  onClick={() => update('isPaid', true)}
                  data-testid="ce-entry-paid"
                  className={`h-9 rounded-md border-2 text-xs font-semibold uppercase tracking-widest ${
                    form.isPaid ? 'border-primary bg-primary/5 text-primary' : 'border-border'
                  }`}
                >
                  Paid
                </button>
              </div>
            </FieldMobile>

            {form.isPaid && (
              <>
                <FieldMobile label="Entry fee (\u20A6 NGN)">
                  <Input
                    data-testid="ce-fee"
                    type="number"
                    inputMode="numeric"
                    value={form.fee}
                    onChange={(e) => update('fee', e.target.value)}
                    placeholder="e.g. 5000"
                    className="h-12 rounded-md text-base font-mono"
                  />
                </FieldMobile>

                <div className="rivl-card p-4 space-y-3 border-primary/40">
                  <div>
                    <p className="rivl-label text-[10px]">Payment details</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      We auto-verify the account name with Paystack once you fill in the bank + number.
                    </p>
                  </div>
                  <BankAccountFields
                    bankName={form.bankName}
                    accountNumber={form.accountNumber}
                    accountName={form.accountName}
                    onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Make it shine</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Add a hero image, description and verification settings.
              </p>
            </div>

            <FieldMobile label="Hero image">
              {form.image ? (
                <div className="relative">
                  <div
                    className="h-44 w-full rounded-md bg-cover bg-center border border-border"
                    style={{ backgroundImage: `url(${form.image})` }}
                    data-testid="ce-image-preview"
                  />
                  <label className="absolute bottom-2 right-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImage}
                      className="hidden"
                      data-testid="ce-image-replace-input"
                    />
                    <span className="inline-flex items-center gap-2 px-3 h-9 rounded-md bg-background/90 backdrop-blur border border-border text-xs font-medium cursor-pointer">
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Replace
                    </span>
                  </label>
                </div>
              ) : (
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImage}
                    className="hidden"
                    data-testid="ce-image-input"
                  />
                  <div className="h-36 w-full rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary transition-colors">
                    {uploading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Tap to upload (max 5MB)</span>
                      </>
                    )}
                  </div>
                </label>
              )}
            </FieldMobile>

            <FieldMobile label="Description">
              <Textarea
                data-testid="ce-description"
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                rows={4}
                placeholder="What makes this run special?"
                className="rounded-md text-sm"
              />
            </FieldMobile>

            {/* Physical events: show map + checkpoints. Virtual events skip these. */}
            {!(form.type === 'community' && form.eventMode === 'virtual') && (
              <>
                <FieldMobile label="Start & finish points">
                  <MapPointPicker
                    start={form.startLat != null && form.startLng != null ? { lat: form.startLat, lng: form.startLng } : null}
                    finish={form.finishLat != null && form.finishLng != null ? { lat: form.finishLat, lng: form.finishLng } : null}
                    checkpoints={form.checkpointsEnabled ? form.checkpoints : []}
                    onChange={({ start, finish, checkpoints }) => setForm((f) => ({
                      ...f,
                      startLat: start?.lat ?? null,
                      startLng: start?.lng ?? null,
                      finishLat: finish?.lat ?? null,
                      finishLng: finish?.lng ?? null,
                      // When checkpoints mode is enabled, accept new picks from the map.
                      // We auto-enable the toggle the first time the organizer drops a checkpoint.
                      checkpointsEnabled: checkpoints && checkpoints.length > 0 ? true : f.checkpointsEnabled,
                      checkpoints: checkpoints != null ? checkpoints : f.checkpoints,
                    }))}
                    height={280}
                  />
                </FieldMobile>

                <div className="rivl-card p-4">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="ce-checkpoints-toggle-label">
                    <input
                      type="checkbox"
                      checked={form.checkpointsEnabled}
                      onChange={(e) => update('checkpointsEnabled', e.target.checked)}
                      className="h-4 w-4 rounded border-border"
                      data-testid="ce-checkpoints-toggle"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">Checkpoints</div>
                      <div className="text-[11px] text-muted-foreground">
                        Add intermediate points runners must pass through
                      </div>
                    </div>
                  </label>

                  {form.checkpointsEnabled && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <CheckpointEditor
                        value={form.checkpoints}
                        onChange={(cps) => update('checkpoints', cps)}
                        country={userCountryCC}
                        start={form.startLat != null && form.startLng != null ? { lat: form.startLat, lng: form.startLng } : null}
                        finish={form.finishLat != null && form.finishLng != null ? { lat: form.finishLat, lng: form.finishLng } : null}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {form.type === 'community' && form.eventMode === 'virtual' && (
              <div className="rivl-card p-4 border-dashed border-2 border-primary/40 bg-primary/5" data-testid="ce-virtual-notice">
                <p className="text-sm font-semibold text-primary">Virtual run</p>
                <p className="text-[12px] text-muted-foreground mt-1">
                  Runners can complete this from anywhere. We&apos;ll track distance and time only — no route validation.
                </p>
              </div>
            )}

            {form.type === 'official' && (
              <>
                <FieldMobile label="Bib mode">
                  <div className="grid grid-cols-3 gap-2">
                    {['none', 'self-assigned', 'validated'].map((m) => (
                      <button
                        key={m}
                        onClick={() => update('bibMode', m)}
                        data-testid={`ce-bib-${m}`}
                        className={`h-11 rounded-md border text-xs font-medium uppercase tracking-wide ${
                          form.bibMode === m
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border'
                        }`}
                      >
                        {m === 'self-assigned' ? 'Self' : m}
                      </button>
                    ))}
                  </div>
                </FieldMobile>

                <div className="rivl-card p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Medal token</div>
                    <div className="text-[11px] text-muted-foreground">QR-issued at finish</div>
                  </div>
                  <Switch
                    checked={form.medalEnabled}
                    onCheckedChange={(v) => update('medalEnabled', v)}
                    data-testid="ce-medal-toggle"
                  />
                </div>

                {form.medalEnabled && (
                  <FieldMobile label="Medal scanner activation code (organizer only)" icon={ShieldCheck}>
                    <div className="flex gap-2">
                      <Input
                        data-testid="ce-activation-code"
                        value={form.activationCode}
                        onChange={(e) => update('activationCode', e.target.value.toUpperCase().slice(0, 8))}
                        placeholder="e.g. RIVL42"
                        className="h-11 rounded-md text-base font-mono tracking-widest"
                        maxLength={8}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => update('activationCode', Math.random().toString(36).slice(2, 8).toUpperCase())}
                        data-testid="ce-activation-generate"
                        className="h-11 rounded-md text-xs"
                      >
                        Generate
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Volunteers enter this code at the finish line to unlock the medal scanner. Don't share with runners.
                    </p>
                  </FieldMobile>
                )}
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Review & launch</h2>
              <p className="text-xs text-muted-foreground mt-1">Make sure everything looks right.</p>
            </div>

            {form.image && (
              <div
                className="h-44 w-full rounded-md bg-cover bg-center border border-border"
                style={{ backgroundImage: `url(${form.image})` }}
              />
            )}

            <div className="rivl-card divide-y divide-border">
              <ReviewRow k="Type" v={form.type === 'official' ? 'Official' : `Community · ${isVirtual ? 'Virtual' : 'Physical'}`} />
              <ReviewRow k="Name" v={form.name || '—'} />
              <ReviewRow k="Location" v={isVirtual ? 'Virtual · run anywhere' : (form.location || '—')} />
              <ReviewRow k="Date" v={form.date ? new Date(form.date).toLocaleString() : '—'} />
              <ReviewRow k="Distance" v={`${form.distance} km`} />
              <ReviewRow k="Participants" v={
                form.capacity === '299' ? 'Free · 0–299' :
                form.capacity === '499' ? 'Pro · 300–499' :
                form.capacity === '1000' ? 'Enterprise · 500–1,000' :
                (form.capacity || 'Unlimited')
              } />
              <ReviewRow k="Entry" v={form.isPaid ? `\u20A6${Number(form.fee || 0).toLocaleString()}` : 'Free'} />
              {form.checkpointsEnabled && form.checkpoints.length > 0 && (
                <ReviewRow k="Checkpoints" v={`${form.checkpoints.length} stops`} />
              )}
              {form.type === 'official' && <ReviewRow k="Bib mode" v={form.bibMode} />}
              {form.type === 'official' && <ReviewRow k="Medal" v={form.medalEnabled ? 'Enabled' : 'Off'} />}
            </div>
          </div>
        )}
      </div>

      {/* Sticky footer button */}
      <div className="sticky bottom-0 px-4 py-3 bg-background border-t border-border">
        {step < STEPS.length - 1 ? (
          <Button
            onClick={goNext}
            disabled={step === 1 && !canContinueBasics}
            className="w-full h-12 rounded-md text-base font-semibold"
            data-testid="ce-next"
          >
            Continue <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            disabled={busy || !canContinueBasics}
            className="w-full h-12 rounded-md text-base font-semibold"
            data-testid="ce-create"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="h-5 w-5 mr-2" /> Launch event</>}
          </Button>
        )}
      </div>
      {pendingNav !== null && (
        <LeaveConfirmDialog
          onCancel={() => setPendingNav(null)}
          onConfirm={() => {
            const target = pendingNav;
            setPendingNav(null);
            // Allow the navigation by clearing dirty state first.
            setTimeout(() => {
              if (typeof target === 'number') nav(target);
              else nav(target);
            }, 0);
          }}
        />
      )}
    </div>
  );
}

function Stepper({ current, total }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 w-5 rounded-full transition-colors ${i <= current ? 'bg-primary' : 'bg-border'}`}
        />
      ))}
    </div>
  );
}

function FieldMobile({ label, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="rivl-label flex items-center gap-1.5 text-[10px]">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </Label>
      {children}
    </div>
  );
}

function TypeCard({ active, onClick, icon: Icon, title, points, primary, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rivl-card p-5 transition-all ${
        active
          ? primary
            ? 'border-primary bg-primary/5'
            : 'border-foreground bg-foreground/5'
          : 'hover:border-primary/60'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`h-11 w-11 rounded-md flex items-center justify-center ${primary ? 'bg-primary text-primary-foreground' : 'bg-foreground text-background'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {points.map((p) => (
              <li key={p}>· {p}</li>
            ))}
          </ul>
        </div>
        {active && (
          <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4" />
          </div>
        )}
      </div>
    </button>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{k}</span>
      <span className="text-sm font-medium font-mono text-right max-w-[60%] truncate">{v}</span>
    </div>
  );
}


function LeaveConfirmDialog({ onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="leave-confirm-dialog"
      onClick={onCancel}
    >
      <div
        className="rivl-card max-w-sm w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">Are you sure you want to leave the page?</h3>
          <p className="text-sm text-muted-foreground mt-2">
            You'll lose everything you've entered. This event won't be saved.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="leave-confirm-no"
            className="h-11"
          >
            No, stay
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            data-testid="leave-confirm-yes"
            className="h-11"
          >
            Yes, leave
          </Button>
        </div>
      </div>
    </div>
  );
}
